"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { useToasts } from "@opensea/ui-kit";
import { useLocalStorage } from "@opensea/ui-kit/hooks";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { isItemTradable, isItemTradableFragment } from "../../../../../../apps/web/components/items/validation/tradable";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants";
import { useSyncFormFields } from "../../../hooks/useSyncFormField";
import { useAddress } from "../../../polychain/hooks";
import { FlowActions, FlowState } from "../../../stores/flowStore";
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange";
import { useLiveUpdateItemsBestOrders, useLiveUpdateItemsBestOrdersFragment } from "../../hooks/useLiveUpdateItemsBestOrders";
import { CreateListingToast } from "../../ToastHandler/CreateListingToast/CreateListingToast";
import { CREATOR_FEE_ENFORCED_LOCAL_STORAGE, DEFAULT_EXPIRATION, LISTING_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, LISTING_EXPIRATION_LOCAL_STORAGE } from "../constants";
import { ListingConfirmPriceModal } from "./components/ListingConfirmPriceModal";
import { ListingFlowDone } from "./components/ListingFlowDone";
import { ListingFlowDone_itemFragment } from "./components/ListingFlowDone/ListingFlowDone.fragment";
import { ListingFlowForm, ListingFlowFormSkeleton } from "./components/ListingFlowForm";
import { ListingFlowForm_itemFragment } from "./components/ListingFlowForm/ListingFlowForm.fragment";
import { readItemHighestTraitFloor } from "./components/ListingFlowItemsTable/readItemHighestTraitFloor";
import { readItemHighestTraitFloorFragment } from "./components/ListingFlowItemsTable/readItemHighestTraitFloor.fragment";
import { ListingFlowReview } from "./components/ListingFlowReview/ListingFlowReview";
import { ListingFlowReview_itemFragment } from "./components/ListingFlowReview/ListingFlowReview.fragment";
import { listingFlowFormSchema } from "./schema";
const ListingFlowBaseFragment = graphql(`
    fragment ListingFlowBase on Item {
      id
      imageUrl
      chain {
        identifier
      }
      collection {
        id
        fees {
          totalCreatorFee {
            feeBasisPoints
            isRequired
          }
          openseaFee {
            feeBasisPoints
          }
        }

        floorPrice {
          pricePerItem {
            token {
              unit
            }
          }
        }
      }
      chain {
        listingCurrency {
          symbol
        }
      }
      isFungible
      ownership(address: $address) {
        quantity
      }
      lowestListingForOwner(address: $address) {
        pricePerItem {
          token {
            unit
          }
        }
      }
      bestListing {
        pricePerItem {
          token {
            unit
          }
        }
      }
      ...ListingFlowForm_item
      ...ListingFlowDone_item
      ...ListingFlowReview_item
      ...useLiveUpdateItemsBestOrders
      ...readItemHighestTraitFloor
    }
  `, [ListingFlowForm_itemFragment, ListingFlowDone_itemFragment, ListingFlowReview_itemFragment, useLiveUpdateItemsBestOrdersFragment, readItemHighestTraitFloorFragment]);
const ListingFlowQuery = graphql(`
    query ListingFlowQuery(
      $identifiers: [ItemIdentifierInput!]!
      $address: Address
    ) {
      itemsByIdentifiers(identifiers: $identifiers) {
        id
        tokenId
        contractAddress
        chain {
          identifier
        }
        ...ListingFlowBase
        ...isItemTradable
      }
    }
  `, [ListingFlowBaseFragment, isItemTradableFragment]);
export function ListingFlow(t0) {
  const $ = _c(12);
  const {
    items: itemsInput
  } = t0;
  const address = useAddress();
  let t1;
  if ($[0] !== itemsInput) {
    t1 = itemsInput.map(_temp);
    $[0] = itemsInput;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== address || $[3] !== t1) {
    t2 = {
      query: ListingFlowQuery,
      variables: {
        identifiers: t1,
        address
      },
      context: NETWORK_ONLY_OPERATION_CONTEXT
    };
    $[2] = address;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [t3] = useQuery(t2);
  const {
    data,
    fetching
  } = t3;
  useEndFlowOnAccountChange();
  const anyItemDisabled = data?.itemsByIdentifiers.some(_temp2);
  if (anyItemDisabled) {
    throw new Error("One or more items disabled for trading.");
  }
  let t4;
  if ($[5] !== data?.itemsByIdentifiers || $[6] !== itemsInput) {
    t4 = data?.itemsByIdentifiers.flatMap(item_1 => {
      const quantity = itemsInput.find(itemInput => itemInput.tokenId === item_1?.tokenId && itemInput.chain.identifier === item_1.chain.identifier && itemInput.contractAddress === item_1.contractAddress)?.quantity;
      return item_1 && quantity ? [{
        ...item_1,
        quantity
      }] : [];
    });
    $[5] = data?.itemsByIdentifiers;
    $[6] = itemsInput;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const items = t4;
  if (fetching) {
    let t5;
    if ($[8] !== itemsInput.length) {
      t5 = <ListingFlowFormSkeleton count={itemsInput.length} />;
      $[8] = itemsInput.length;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  if (!items) {
    throw new Error("There was a problem with listing.");
  }
  let t5;
  if ($[10] !== items) {
    t5 = <ListingFlowBase items={items} />;
    $[10] = items;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp2(item_0) {
  return !isItemTradable(item_0);
}
function _temp(item) {
  return {
    tokenId: item.tokenId,
    contractAddress: item.contractAddress,
    chain: item.chain.identifier
  };
}
const ListingFlowBase = t0 => {
  const $ = _c(50);
  const {
    items: itemKeys
  } = t0;
  const t = useTranslations("wallet.ListingFlow");
  const {
    addCustomToast
  } = useToasts();
  const [view, setView] = useState("create");
  const {
    flow
  } = useSnapshot(FlowState);
  const mode = flow?.mode;
  const [isPriceWarningModalOpen, setIsPriceWarningModalOpen] = useState(false);
  let t1;
  if ($[0] !== itemKeys) {
    t1 = itemKeys.map(_temp3);
    $[0] = itemKeys;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = useLiveUpdateItemsBestOrders(t1);
  const t2 = useDefaultFormValues(items);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = zodResolver(listingFlowFormSchema);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = {
      defaultValues: t2,
      mode: "onChange",
      resolver: t3
    };
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const methods = useForm(t4);
  let t5;
  if ($[5] !== methods.control) {
    t5 = {
      control: methods.control,
      name: "listings"
    };
    $[5] = methods.control;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const listings = useWatch(t5);
  let t6;
  let t7;
  if ($[7] !== items || $[8] !== listings) {
    let t8;
    if ($[10] !== listings) {
      t8 = item_0 => listings.find(listing => listing.id === item_0.id);
      $[10] = listings;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    t7 = items.filter(t8);
    $[7] = items;
    $[8] = listings;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  t6 = t7;
  const listingItems = t6;
  let t8;
  if ($[12] !== addCustomToast || $[13] !== listingItems || $[14] !== listings[0]) {
    t8 = success => {
      addCustomToast(() => <CreateListingToast imageUrls={listingItems.map(_temp4)} price={{
        unit: listings[0].price,
        symbol: listings[0].listingSymbol
      }} success={success} />, {
        id: "create-listing-toast",
        duration: 15000
      });
    };
    $[12] = addCustomToast;
    $[13] = listingItems;
    $[14] = listings[0];
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const showToast = t8;
  let t9;
  if ($[16] !== items) {
    t9 = items.flatMap(_temp5);
    $[16] = items;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== methods.setValue || $[19] !== t9) {
    t10 = {
      fields: t9,
      setValue: methods.setValue
    };
    $[18] = methods.setValue;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  useSyncFormFields(t10);
  let t11;
  if ($[21] !== listings) {
    t11 = () => {
      const highSevPriceWarningsCount = listings.filter(_temp6).length;
      if (highSevPriceWarningsCount > 0) {
        setIsPriceWarningModalOpen(true);
      } else {
        setView("review");
      }
    };
    $[21] = listings;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const handleFormSubmit = t11;
  let t12;
  if ($[23] !== handleFormSubmit || $[24] !== items || $[25] !== view) {
    t12 = view === "create" && <ListingFlowForm items={items} onSubmit={handleFormSubmit} />;
    $[23] = handleFormSubmit;
    $[24] = items;
    $[25] = view;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== listingItems || $[28] !== mode || $[29] !== showToast || $[30] !== view) {
    t13 = view === "review" && <ListingFlowReview items={listingItems} onBack={() => setView("create")} onError={() => {
      if (mode === "minimized") {
        showToast(false);
        FlowActions.endFlow();
      }
      setView("error");
    }} onSuccess={() => {
      FlowActions.onSuccess();
      if (mode === "minimized") {
        showToast(true);
        FlowActions.endFlow();
      }
      setView("success");
    }} />;
    $[27] = listingItems;
    $[28] = mode;
    $[29] = showToast;
    $[30] = view;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== listingItems || $[33] !== t || $[34] !== view) {
    t14 = view === "success" && <ListingFlowDone items={listingItems} title={t("successTitle")} withLink={true} />;
    $[32] = listingItems;
    $[33] = t;
    $[34] = view;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== listingItems || $[37] !== t || $[38] !== view) {
    t15 = view === "error" && <ListingFlowDone body={t("errorBody")} items={listingItems} title={t("errorTitle")} withError={true} />;
    $[36] = listingItems;
    $[37] = t;
    $[38] = view;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setView("review");
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== isPriceWarningModalOpen) {
    t17 = <ListingConfirmPriceModal onNext={t16} open={isPriceWarningModalOpen} setIsOpen={setIsPriceWarningModalOpen} />;
    $[41] = isPriceWarningModalOpen;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== methods || $[44] !== t12 || $[45] !== t13 || $[46] !== t14 || $[47] !== t15 || $[48] !== t17) {
    t18 = <FormProvider {...methods}>{t12}{t13}{t14}{t15}{t17}</FormProvider>;
    $[43] = methods;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  return t18;
};
const useDefaultFormValues = items => {
  const $ = _c(18);
  const [localStorageCreatorFeeEnforced] = useLocalStorage(CREATOR_FEE_ENFORCED_LOCAL_STORAGE, true);
  const [localStorageListingExpiration] = useLocalStorage(LISTING_EXPIRATION_LOCAL_STORAGE, DEFAULT_EXPIRATION);
  const [localStorageListingExpirationCustom] = useLocalStorage(LISTING_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, undefined);
  let t0;
  if ($[0] !== items || $[1] !== localStorageCreatorFeeEnforced) {
    t0 = items.some(_temp7) || localStorageCreatorFeeEnforced;
    $[0] = items;
    $[1] = localStorageCreatorFeeEnforced;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== localStorageListingExpirationCustom) {
    t1 = localStorageListingExpirationCustom ? new Date(localStorageListingExpirationCustom) : undefined;
    $[3] = localStorageListingExpirationCustom;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== localStorageListingExpiration || $[6] !== t1) {
    t2 = {
      expirationType: localStorageListingExpiration,
      customExpiration: t1
    };
    $[5] = localStorageListingExpiration;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      identifier: undefined,
      resolvedAddress: undefined,
      isResolving: false,
      currentAddress: undefined
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== items || $[10] !== localStorageCreatorFeeEnforced) {
    let t5;
    if ($[12] !== localStorageCreatorFeeEnforced) {
      t5 = item_0 => {
        const ownedQuantity = item_0.ownership?.quantity ?? "0";
        return {
          id: item_0.id,
          quantity: item_0.quantity,
          creatorFeeBasisPoints: item_0.collection?.fees.totalCreatorFee.feeBasisPoints || 0,
          openSeaFeeBasisPoints: item_0.collection?.fees.openseaFee?.feeBasisPoints || 0,
          price: item_0.lowestListingForOwner?.pricePerItem.token.unit || Number.NaN,
          floorPrice: item_0.isFungible ? item_0.bestListing?.pricePerItem.token.unit : item_0.collection?.floorPrice?.pricePerItem.token.unit,
          isCreatorFeeRequired: Boolean(item_0.collection?.fees.totalCreatorFee.isRequired),
          applyCreatorFee: item_0.collection?.fees.totalCreatorFee.isRequired || localStorageCreatorFeeEnforced,
          ownedQuantity: item_0.isFungible ? +ownedQuantity : 1,
          listingSymbol: item_0.chain.listingCurrency.symbol
        };
      };
      $[12] = localStorageCreatorFeeEnforced;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = items.map(t5);
    $[9] = items;
    $[10] = localStorageCreatorFeeEnforced;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[14] !== t0 || $[15] !== t2 || $[16] !== t4) {
    t5 = {
      adjustForFees: false,
      applyCreatorFee: t0,
      expiration: t2,
      reservedBuyer: t3,
      listings: t4
    };
    $[14] = t0;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
function _temp3(item) {
  return {
    ...readFragment(ListingFlowBaseFragment, item),
    quantity: item.quantity
  };
}
function _temp4(item_1) {
  return item_1.imageUrl || "";
}
function _temp5(item_2, index) {
  return [{
    name: `listings.${index}.floorPrice`,
    value: item_2.isFungible ? item_2.bestListing?.pricePerItem.token.unit : item_2.collection?.floorPrice?.pricePerItem.token.unit
  }, {
    name: `listings.${index}.traitFloorPrice`,
    value: readItemHighestTraitFloor(item_2)?.token.unit
  }];
}
function _temp6(listing_0) {
  return listing_0.shouldConfirmPrice;
}
function _temp7(item) {
  return item.collection?.fees.totalCreatorFee.isRequired;
}